/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { TableCell } from '@squiz/sds';
import { DataTable } from 'apps/monitoring/src/Components/DataTable/DataTable';
import { DataTableProps } from 'apps/monitoring/src/Components/DataTable/DataTable.props';
import { MONITORING_STRINGS } from 'apps/monitoring/src/Constants/Strings.constants';
import { formatBytes } from 'apps/monitoring/src/Utilities/FormatBytes/FormatBytes';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FILE_SIZE_TABLE_STRINGS } from './FileSizeTable.constants';
import { FileSizeTableProps } from './FileSizeTable.props';

export const FileSizeTable = ({ data, time, from, to, displayRange }: FileSizeTableProps): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const domain = atob(location.pathname.split('/').pop() as string);
  const labels = {
    bandwidth: t(FILE_SIZE_TABLE_STRINGS.bandwidth),
    fileSize: t(FILE_SIZE_TABLE_STRINGS.fileSize),
    heading: t(FILE_SIZE_TABLE_STRINGS.heading),
    pageURL: t(MONITORING_STRINGS.pageURL),
    requests: t(FILE_SIZE_TABLE_STRINGS.requests),
  };

  const columns: Array<TableCell> = [
    { content: labels.pageURL, key: `url` },
    { content: labels.fileSize, key: 'fileSize' },
    { content: labels.requests, key: 'requests' },
    { content: labels.bandwidth, key: 'bandwidth' },
  ];

  const constructRows = useMemo((): Array<Object> => {
    const numberFormatter = new Intl.NumberFormat('en-US');
    if (!data) {
      return [];
    }

    return data.map(({ bandwidth, fileSize, requests, path }) => {
      return {
        bandwidth: formatBytes(parseInt(bandwidth.data)),
        fileSize: formatBytes(parseInt(fileSize.data)),
        requests: numberFormatter.format(Number(requests.data)),
        url: (
          <a className="monitoring-data-table__url" href={`https://${domain}${path}`} rel="noreferrer" target="_blank">
            {path}
          </a>
        ),
      };
    });
  }, [data, domain]);

  const dataTableProps: DataTableProps = {
    columns,
    itemsPerPage: 50,
    rows: constructRows,
    title: labels.heading,
    ...(displayRange
      ? {
          from,
          to,
        }
      : { time }),
  };
  return <DataTable {...dataTableProps} />;
};
